import { useState } from "react";
import { ProjectModal } from "./ProjectModal";

const TagChip = ({ tag }) => (
	<span className="inline-block text-[11px] font-mono font-medium px-2.5 py-1 rounded-full bg-[#f0f0f0] dark:bg-[#111] text-[#666] dark:text-[#555] border border-[#e8e8e8] dark:border-[#1c1c1c]">
		{tag}
	</span>
);

const ExternalLinkIcon = () => (
	<svg
		className="w-3.5 h-3.5"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round">
		<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
		<polyline points="15 3 21 3 21 9" />
		<line x1="10" y1="14" x2="21" y2="3" />
	</svg>
);

const GithubIcon = () => (
	<svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
		<path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
	</svg>
);

export const ProjectCard = ({ project }) => {
	const [imgError, setImgError] = useState(false);
	const [showModal, setShowModal] = useState(false);

	return (
		<>
			<article className="group flex flex-col rounded-xl overflow-hidden border border-[#ebebeb] dark:border-[#111] bg-white dark:bg-[#080808] hover:border-[#ccc] dark:hover:border-[#1e1e1e] transition-all duration-150 hover:-translate-y-[] hover:shadow-lg dark:hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)]">
				{/* Thumbnail */}
				<div className="relative overflow-hidden bg-[#f5f5f5] dark:bg-[#0d0d0d] h-48">
					{!imgError ? (
						<img
							src={project.thumbnail_url}
							alt={`${project.name} thumbnail`}
							onError={() => setImgError(true)}
							className="w-full h-full object-cover"
						/>
					) : (
						<div className="w-full h-full flex items-center justify-center text-[#ccc] dark:text-[#333]">
							<svg
								className="w-10 h-10"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1">
								<rect x="3" y="3" width="18" height="18" rx="2" />
								<circle cx="8.5" cy="8.5" r="1.5" />
								<polyline points="21 15 16 10 5 21" />
							</svg>
						</div>
					)}
					{project.year && (
						<span className="absolute top-3 right-3 text-[10px] font-mono bg-black/60 dark:bg-white/10 text-white backdrop-blur-sm px-2 py-0.5 rounded-full">
							{project.year}
						</span>
					)}
				</div>

				{/* Body */}
				<div className="flex flex-col flex-1 p-5 gap-3">
					<div>
						<h4 className="font-poppins font-semibold text-[15px] text-black dark:text-[#D8D8D8] leading-snug mb-1.5">
							{project.name}
						</h4>
						{project.role && (
							<p className="text-[11px] font-mono text-[#aaa] dark:text-[#444] uppercase tracking-wider mb-2">
								{project.role}
							</p>
						)}
						<p className="text-[13px] text-[#666] dark:text-[#555] font-lato leading-relaxed line-clamp-3">
							{project.shortDesc || project.description}
						</p>
					</div>

					{/* Tags */}
					{project.tags?.length > 0 && (
						<div className="flex flex-wrap gap-1.5 mt-auto pt-2">
							{project.tags.slice(0, 5).map((tag, i) => (
								<TagChip key={i} tag={tag} />
							))}
						</div>
					)}

					{/* Actions */}
					<div className="flex items-center gap-3 pt-2 border-t border-[#f0f0f0] dark:border-[#111] mt-auto">
						<button
							onClick={() => setShowModal(true)}
							className="inline-flex items-center gap-1.5 text-[12px] font-poppins font-medium text-black dark:text-[#ccc] hover:text-[#444] dark:hover:text-white transition-colors">
							<svg
								className="w-3.5 h-3.5"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2">
								<circle cx="11" cy="11" r="8" />
								<line x1="21" y1="21" x2="16.65" y2="16.65" />
							</svg>
							View Details
						</button>

						<span className="text-[#ddd] dark:text-[#222]">·</span>

						<a
							href={project.web_link}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex items-center gap-1.5 text-[12px] font-poppins font-medium text-[#888] dark:text-[#444] hover:text-black dark:hover:text-[#ccc] transition-colors">
							<ExternalLinkIcon />
							Live
						</a>

						{project.github_link && (
							<>
								<span className="text-[#ddd] dark:text-[#222]">·</span>
								<a
									href={project.github_link}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center gap-1.5 text-[12px] font-poppins font-medium text-[#888] dark:text-[#444] hover:text-black dark:hover:text-[#ccc] transition-colors">
									<GithubIcon />
									Source
								</a>
							</>
						)}
					</div>
				</div>
			</article>

			{showModal && (
				<ProjectModal project={project} onClose={() => setShowModal(false)} />
			)}
		</>
	);
};
